import { useAuthStore } from '~/stores/auth';
import type {
  AuthSession,
  CheckUsernameResponse,
  User,
} from '~/types/auth/User';
import type { ApiInstance, LaravelApiResponse } from '~/types/plugins/api';

export const useAuthSession = () => {
  async function signIn({
    username,
    password,
    inviteCode,
  }: {
    username: string;
    password: string;
    inviteCode?: string;
  }) {
    const { signIn: handleSignIn, data: response } = useAuth();
    const { rawToken } = useAuthState();
    const { refreshSession, setAccessToken } = useAuthStore();

    try {
      const result = await handleSignIn(
        { username, password, invite_code: inviteCode },
        { callbackUrl: '/', redirect: false, callGetSession: false },
      );

      if (result?.error) {
        return { data: null, error: result.error };
      }

      // Persist the raw (unprefixed) auth token to a cookie so it survives
      // page reloads. sidebase prefixes it with "Bearer " on every read
      // (formatToken), so storing the already-prefixed token here would
      // double up the prefix on the next page load and break /auth/me.
      if (rawToken.value) {
        setAccessToken(rawToken.value);
      }

      await refreshSession();

      return { data: response.value, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  }

  const fetchSession = async (): Promise<AuthSession | null> => {
    try {
      const { $apiClient } = useNuxtApp() as unknown as {
        $apiClient: ApiInstance;
      };
      const result = await $apiClient.get<AuthSession>('auth/me');

      if (result) {
        const userData = {
          user: result.data as User,
        } as AuthSession;

        return userData;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Failed to fetch session:', error);
      return null;
    }
  };

  async function signOut() {
    const { signOut: handleSignOut } = useAuth();
    const { clearSession } = useAuthStore();

    try {
      await handleSignOut({
        callbackUrl: '/login',
        redirect: false,
      });

      clearSession();
    } catch (error) {
      console.error('Sign out error:', error);

      clearSession();
    } finally {
      // Purge all cached async data (e.g. subscription plan, payment methods)
      // so a subsequent login on the same browser session never serves the
      // previous user's data.
      clearNuxtData();
    }
  }

  async function signUp({
    username,
    password,
    passwordConfirmation,
    inviteCode,
  }: {
    username: string;
    password: string;
    passwordConfirmation: string;
    inviteCode?: string;
  }) {
    const { $apiClient } = useNuxtApp() as unknown as {
      $apiClient: ApiInstance;
    };

    try {
      const response = await $apiClient.post<
        LaravelApiResponse<Record<string, unknown>>
      >('auth/register', {
        // If has inviteCode exclude email from payload since backend will use invite email as username
        email: inviteCode ? undefined : username,
        password,
        password_confirmation: passwordConfirmation,
        invite_code: inviteCode,
      });

      if (response?.success === false || response?.errors) {
        return {
          data: undefined,
          error: { data: response },
        };
      }

      return await signIn({
        username,
        password,
      });
    } catch (error) {
      return { data: undefined, error: error };
    }
  }

  async function checkUsername(
    username: string,
  ): Promise<CheckUsernameResponse | null> {
    const { $apiClient } = useNuxtApp() as unknown as {
      $apiClient: ApiInstance;
    };

    try {
      const response = await $apiClient.post<
        LaravelApiResponse<CheckUsernameResponse>
      >('auth/check-username', {
        username,
      });
      if (!response.success) {
        return null;
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_error) {
      return null;
    }
  }

  return {
    signIn,
    fetchSession,
    signOut,
    signUp,
    checkUsername,
  };
};
